import { useMemo, useState } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { AccountDocumentType } from 'generated/graphql';
import DocumentSignposting from 'questionFlow/genericQuestions/DocumentSignposting';
import {
  IdentityQuestionComponentProps,
  StepNamesDefaultIdentity,
} from 'questionFlow/flows/defaultIdentity/types';
import { get } from 'lodash';
import { Checkbox, DateInput, Field } from 'components';
import { OnDateChange } from 'components/molecules/DateInput/DateInput';
import { SubmitValue, ValidationRule } from 'questionFlow/types';
import { getDocTypeForCopy } from 'questionFlow/genericQuestions/Upload/utils';
import { ONFIDO_FACIAL_SCAN_POLICY_URL, ONFIDO_TERMS_OF_SERVICE_URL } from 'services/onfidoSdk';

const validate = (v: string, rules: ValidationRule[]) => rules.find(({ rule }) => !rule(v));

const cmsDocumentLocation = '/questions/identity/documents';
const cmsSignpostingLocation = '/questions/identity/documentSignposting';

const useContent = (t: TFunction<'translation'>, fileName: AccountDocumentType) => {
  const translationLocation: Partial<Record<AccountDocumentType | string, string>> = {
    [AccountDocumentType.UkDrivingLicence]: t(`${cmsDocumentLocation}.UK_DRIVING_LICENCE`),
    [AccountDocumentType.DrivingLicence]: t(`${cmsDocumentLocation}.DRIVING_LICENCE`),
    [AccountDocumentType.Passport]: t(`${cmsDocumentLocation}.PASSPORT`),
    [AccountDocumentType.UkPassport]: t(`${cmsDocumentLocation}.UK_PASSPORT`),
    [AccountDocumentType.TimeLimitedPassport]: t(`${cmsDocumentLocation}.TIME_LIMITED_PASSPORT`),
    NATIONAL_ID: t(`${cmsDocumentLocation}.NATIONAL_ID`),
    VISA: t(`${cmsDocumentLocation}.VISA`),
  };

  const doesTranslationExist = (translateItem: string) => {
    return t(`${cmsSignpostingLocation}.${fileName}.${translateItem}` as any).includes(
      cmsSignpostingLocation,
    )
      ? {}
      : {
          [translateItem]: t(`${cmsSignpostingLocation}.${fileName}.${translateItem}` as any, {
            doc: translationLocation[fileName] || '{{doc}}',
          }),
        };
  };

  return {
    ...doesTranslationExist('title'),
    ...doesTranslationExist('subtitle'),
    ...doesTranslationExist('doItems'),
    ...doesTranslationExist('dontItems'),
    ...translationLocation,
    expiryDateTitle: t(`${cmsSignpostingLocation}.expiryDateTitle`),
    expiryDateSubtitle: t(`${cmsSignpostingLocation}.expiryDateSubtitle`),
  };
};

const IdentityDocSignposting = ({
  documentKey = StepNamesDefaultIdentity.documents,
  onSubmit,
  validationRules,
  side,
  ...props
}: IdentityQuestionComponentProps & { documentKey?: string; side?: 'front' | 'back' }) => {
  const { t } = useTranslation();

  const [error, setError] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const docType = useMemo(
    () => get({ ...props.values, livePhotoSignposting: 'LIVE_PHOTO' }, documentKey),
    [documentKey, props.values],
  );

  const [checked, setChecked] = useState(false);
  const [onfidoError, setOnfidoError] = useState('');
  const translatedContent = useContent(t, docType as AccountDocumentType);

  const handleDateChange: OnDateChange = ({ value, valid }) => {
    setExpiryDate(value);
    if (valid && error) {
      setError('');
    }
  };

  const validateDateInput = () => {
    const result = validate(expiryDate, validationRules.expiryDate);
    setError(result ? t(result.error as any) : '');
  };

  const handleSubmit = (params: SubmitValue) => {
    if (docType === AccountDocumentType.LivePhoto && !checked) {
      setOnfidoError('This field is required');
      return;
    }

    if (error) {
      return;
    }

    if (docType === AccountDocumentType.TimeLimitedPassport && side === 'back') {
      if (!expiryDate) {
        validateDateInput();
        return;
      }
      var now = new Date();
      var expiry = new Date(expiryDate);
      onSubmit({ key: 'docSignposting', value: now < expiry });
    } else {
      onSubmit(params);
    }
  };

  const content = useMemo(() => {
    const type = getDocTypeForCopy(docType, side);

    return {
      ...translatedContent,
      fileName: translatedContent[type as keyof typeof translatedContent] as string,
      formattedFileName: translatedContent[type as keyof typeof translatedContent] as string,
    };
  }, [translatedContent, docType, side]);

  return (
    <DocumentSignposting
      {...props}
      showNumericSides={docType === AccountDocumentType.TimeLimitedPassport}
      documentKey={documentKey}
      content={content}
      validationRules={validationRules}
      onSubmit={handleSubmit}
      side={side}
    >
      {docType === AccountDocumentType.TimeLimitedPassport && side === 'back' ? (
        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-2xl font-semibold">{content.expiryDateTitle}</h2>
          <p className="mt-1 mb-4">{content.expiryDateSubtitle}</p>
          <Field data-testid="visa-expiry" validationError={error}>
            <DateInput
              defaultValue={expiryDate}
              data-testid="expity-date-input"
              onChange={handleDateChange}
              onBlur={validateDateInput}
              error={!!error}
              id="startDate"
              dayLabel=""
              monthLabel=""
              yearLabel=""
            />
          </Field>
        </div>
      ) : null}
      {docType === AccountDocumentType.LivePhoto && (
        <div>
          <Field
            data-testid="signposting-field"
            validationError={onfidoError}
            className="py-8 ml-9"
          >
            <label className="text-sm flex" htmlFor="onfido-consent-checkbox">
              <Checkbox
                id="onfido-consent-checkbox"
                className="-ml-9"
                checked={checked}
                onChange={e => setChecked(e.currentTarget.checked)}
                color="blue"
              />

              <span className="ml-4">
                <p>
                  Homeppl uses a third party service provider, Onfido Limited, to provide its
                  identity verification check. Onfido may collect your personal data (including
                  biometric identifiers and/or biometric information) for the purpose of providing
                  this check. I agree that I have read, understood, and accepted{' '}
                  <a
                    className="text-blue-500"
                    rel="noreferrer"
                    target="_blank"
                    href={ONFIDO_FACIAL_SCAN_POLICY_URL}
                  >
                    Onfido Facial Scan & Voice Recording Policy
                  </a>{' '}
                  and{' '}
                  <a
                    className="text-blue-500"
                    rel="noreferrer"
                    target="_blank"
                    href={ONFIDO_TERMS_OF_SERVICE_URL}
                  >
                    Terms of Service
                  </a>
                  , and grant consent to Onfido to process my Biometric Data and other Personal Data
                  for the purposes described in the Policy.<span className="text-red-500">*</span>
                </p>
              </span>
            </label>
          </Field>
        </div>
      )}
    </DocumentSignposting>
  );
};

export default IdentityDocSignposting;
